import { BREAKPOINTS } from '@depop/web-thrift-ui/theme/default/breakpoints.constants';

import {
  type ListProduct,
  type PictureFormat,
  type Preview,
  type ProductPicture,
  type ProductVideo,
  type ThumbnailFormat,
} from '../product/types';
import { type PresApiSearchProduct } from '../search/aliases';

type PresApiPicture = NonNullable<PresApiSearchProduct['pictures']>[number];

/*
  This function will always try to return an image that
  is at least equal in size to the target width. If there isn't it will
  then return the nearest size, even if it's smaller.
*/
export function getNearestSizePictureFormat(
  pictureFormats: ThumbnailFormat[] | undefined,
  width: number
) {
  if (!pictureFormats?.length) {
    return undefined;
  }

  function nearestSize(a: ThumbnailFormat, b: ThumbnailFormat) {
    return Math.abs(b.width - width) < Math.abs(a.width - width) ? b : a;
  }

  const filtered = pictureFormats.filter((a) => a.width >= width);
  if (filtered.length) {
    return filtered.reduce(nearestSize);
  }
  return pictureFormats.reduce(nearestSize);
}

export function filterRequiredPictures(
  pictures: Partial<PictureFormat>[]
): PictureFormat[];
export function filterRequiredPictures(
  pictures: Partial<ThumbnailFormat>[]
): ThumbnailFormat[];
/* eslint-disable @typescript-eslint/no-explicit-any */
export function filterRequiredPictures(pictures: Partial<any>[] = []): any[] {
  return pictures.reduce<ThumbnailFormat[] | PictureFormat[]>(
    (acc, picture) => {
      const { url, height, width, id } = picture;
      if (height && url && width) {
        if (id) {
          return [...acc, { id, url, height, width }];
        }
        return [...acc, { url, height, width }];
      }
      return acc;
    },
    []
  );
}

export function createSrcSet(preview: Preview) {
  return Object.entries(preview)
    .map(([width, url]) => `${url} ${width}w`)
    .join(', ');
}

function getSmallestPreviewImage(preview: Preview) {
  /*
      In the future P10 images will be returned from the BE.
      They do actually exist though and so for now we just
      want to experiment with them
    */
  const fullSizeImageUrl = preview['1280'];
  if (!fullSizeImageUrl) {
    return;
  }

  const path = fullSizeImageUrl.substring(0, fullSizeImageUrl.lastIndexOf('/'));
  return path ? `${path}/P10.jpg` : undefined;
}

export function buildProductImageBasicProps(preview: Preview) {
  return {
    srcSet: createSrcSet(preview),
    width: 245,
    height: 245,
    src: preview['1280'],
    sizes: `
      (min-width: ${BREAKPOINTS.XL_MIN_PX}) 220px,
      (min-width: ${BREAKPOINTS.MD_MIN_PX}) 245px,
      150px`,
  };
}

export function buildProductImageProps({
  preview,
  sellerUserName,
}: Pick<ListProduct, 'preview' | 'sellerUserName'>) {
  return {
    ...buildProductImageBasicProps(preview),
    blurImageSrc: getSmallestPreviewImage(preview),
    ...(!!sellerUserName
      ? { alt: `item listed by ${sellerUserName}` }
      : { alt: '' }),
  };
}

export function picturePreviewAdapter(
  picture?: ProductPicture | PresApiPicture
): Preview {
  if (!picture) {
    return {};
  }

  return Object.values(picture.formats).reduce(
    (prev, format) => (format ? { ...prev, [format.width]: format.url } : prev),
    {}
  );
}

export function videoPreviewAdapter(video: ProductVideo): Preview {
  return Object.values(video.thumbnail.formats).reduce((prev, format) => {
    return { ...prev, [format.width]: format.url };
  }, {});
}

export function productPictureToPictureFormat(
  picture: ProductPicture
): PictureFormat[] {
  return Object.values(picture.formats).map((format) => ({
    id: picture.id,
    url: format.url,
    width: format.width,
    height: format.height,
  }));
}

export function productVideoToPictureFormat(
  video: ProductVideo
): PictureFormat[] {
  return Object.values(video.thumbnail.formats).map((format) => ({
    id: Number(video.id),
    url: format.url,
    width: format.width,
    height: format.height,
  }));
}

// New helpers updating images logic to deal with pres-api Picture format.

function createSearchProductSrcSet(picture: PresApiPicture): string {
  return Object.values(picture.formats)
    .filter((format) => format !== undefined)
    .map((format) => `${format.url} ${format.width}w`)
    .join(', ');
}

function getSearchProductBlurImage(
  picture: PresApiPicture
): string | undefined {
  const fullSizeUrl = picture.formats.P8?.url || picture.formats.P0?.url;
  if (!fullSizeUrl) {
    return;
  }
  const path = fullSizeUrl.substring(0, fullSizeUrl.lastIndexOf('/'));
  return path ? `${path}/P10.jpg` : undefined;
}

export function buildSearchProductImageBasicProps(picture: PresApiPicture) {
  return {
    srcSet: createSearchProductSrcSet(picture),
    width: 245,
    height: 245,
    src:
      picture.formats.P8?.url ||
      picture.formats.P7?.url ||
      picture.formats.P0?.url,
    sizes: `
      (min-width: ${BREAKPOINTS.XL_MIN_PX}) 220px,
      (min-width: ${BREAKPOINTS.MD_MIN_PX}) 245px,
      150px`,
  };
}

export function buildSearchProductImageProps(picture: PresApiPicture) {
  return {
    ...buildSearchProductImageBasicProps(picture),
    blurImageSrc: getSearchProductBlurImage(picture),
  };
}
